/**
 * Free-scan-to-account claim handshake — dashboard side of OVE-4.
 *
 * The server-rendered free-tool guest app offers a "Create free account" CTA the
 * moment the visitor's first note lands (the aha, OVE-3), linking to
 *   ${appUrl}/sign-up?claim=<reviewToken>&source=free-tool-annotate
 * built by buildFreeToolClaimUrl in
 * packages/server/src/review-guest-app/sections/0205-free-tool-claim-cta.js.
 *
 * /sign-up redirects to /sign-in preserving the query string, but those params
 * are then dropped on the post-auth navigate() and lost entirely on the Google
 * OAuth full-page redirect. So we STASH them in sessionStorage the moment the
 * sign-in page mounts (captureFreeScanClaimParams) and CONSUME them after
 * bootstrap creates the account (useFreeScanClaim -> POST /api/review/:token/claim,
 * which re-parents the anonymous session's project + issues into the new team).
 *
 * This module is the SINGLE source of truth for the stash so the capture and the
 * consume can never read/write different keys. It is intentionally free of React
 * and window-globals in its inputs (search + now are passed in) so it is
 * exhaustively unit-testable, mirroring lib/installLanding.ts.
 */
import { log } from '@/lib/logger';

const TOKEN_KEY = 'oqa_free_scan_claim_token';
const SOURCE_KEY = 'oqa_free_scan_claim_source';
const STAMP_KEY = 'oqa_free_scan_claim_ts';

/**
 * A stash older than this is treated as absent. It bounds mis-attachment in a
 * shared browser: if a visitor stashes a token then abandons, a DIFFERENT
 * person signing in on the same tab-session an hour later does not silently
 * inherit the first visitor's scan. The happy path consumes the stash within
 * seconds, so this never affects a real conversion.
 */
export const FREE_SCAN_CLAIM_TTL_MS = 60 * 60 * 1000; // 1 hour

/** Server caps signupSource at 64 chars (bootstrapSchema, auth-shared.ts). */
const SOURCE_MAX = 64;
/** Review tokens are short; cap defensively so a junk URL cannot bloat storage. */
const TOKEN_MAX = 200;

export interface FreeScanClaim {
  token: string;
  source: string | null;
}

function readStamp(): number | null {
  try {
    const raw = sessionStorage.getItem(STAMP_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function isFresh(now: number): boolean {
  const stamp = readStamp();
  if (stamp == null) return false;
  return now - stamp < FREE_SCAN_CLAIM_TTL_MS;
}

/**
 * Stash the claim token (+ source) from the sign-in URL so they survive Clerk's
 * OAuth full-page redirect and the post-auth navigate(), both of which drop the
 * query string. Called at /sign-in mount. `?source=` is only stashed alongside a
 * token, so an unrelated `?source=` on some future link can never leak onto a
 * normal signup's account_created attribution. With no claim token, the link's
 * `utm_source` (the free-tool wall's sign-up button, the marketing CTAs) is
 * stashed as the attribution source alone, never as a claim, and never over a
 * fresh claim (Emily, 2026-10-25). Returns what it captured.
 */
export function captureFreeScanClaimParams(
  search: string,
  now: number = Date.now(),
): { token: string | null; source: string | null } {
  let token: string | null = null;
  let source: string | null = null;
  let utmSource: string | null = null;
  try {
    const params = new URLSearchParams(search);
    const rawToken = params.get('claim');
    token = rawToken && rawToken.length > 0 && rawToken.length <= TOKEN_MAX ? rawToken : null;
    const rawSource = params.get('source');
    source = rawSource ? rawSource.slice(0, SOURCE_MAX) : null;
    const rawUtm = params.get('utm_source')?.trim();
    utmSource = rawUtm ? rawUtm.slice(0, SOURCE_MAX) : null;
  } catch {
    // A malformed search string must not crash the sign-in page.
    return { token: null, source: null };
  }

  if (!token) return captureLinkSource(utmSource, now);

  try {
    sessionStorage.setItem(TOKEN_KEY, token);
    if (source) sessionStorage.setItem(SOURCE_KEY, source);
    else sessionStorage.removeItem(SOURCE_KEY);
    sessionStorage.setItem(STAMP_KEY, String(now));
    log.info('[freeScanClaim] captured claim params', { hasToken: true, source });
  } catch {
    // sessionStorage throws in private mode — the claim simply will not carry.
  }
  return { token, source };
}

/** Attribution-only stash: no token, so readFreeScanClaim still returns null. */
function captureLinkSource(
  utmSource: string | null,
  now: number,
): { token: null; source: string | null } {
  if (!utmSource) return { token: null, source: null };
  try {
    if (sessionStorage.getItem(TOKEN_KEY) && isFresh(now)) return { token: null, source: null };
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.setItem(SOURCE_KEY, utmSource);
    sessionStorage.setItem(STAMP_KEY, String(now));
    log.info('[freeScanClaim] captured link source', { source: utmSource });
  } catch {
    // sessionStorage throws in private mode — the signup simply reads 'direct'.
    return { token: null, source: null };
  }
  return { token: null, source: utmSource };
}

/** Read the stashed claim (token required). Clears + returns null when stale. */
export function readFreeScanClaim(now: number = Date.now()): FreeScanClaim | null {
  let token: string | null = null;
  let source: string | null = null;
  try {
    token = sessionStorage.getItem(TOKEN_KEY);
    source = sessionStorage.getItem(SOURCE_KEY);
  } catch {
    return null;
  }
  if (!token) return null;
  if (!isFresh(now)) {
    clearFreeScanClaim();
    return null;
  }
  return { token, source: source ?? null };
}

/**
 * Read only the attribution source, for the account_created tag on bootstrap.
 * Stale-guarded so a source can never outlive its 1h window either.
 */
export function readStashedSignupSource(now: number = Date.now()): string | null {
  try {
    const source = sessionStorage.getItem(SOURCE_KEY);
    if (!source) return null;
    if (!isFresh(now)) {
      clearFreeScanClaim();
      return null;
    }
    return source;
  } catch {
    return null;
  }
}

export function clearFreeScanClaim(): void {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(SOURCE_KEY);
    sessionStorage.removeItem(STAMP_KEY);
  } catch {
    // ignore — nothing to clear if storage is unavailable
  }
}
